import React from "react";
import moment from "moment";
import { saveAs } from "file-saver";

const fmt = (m) => m.format("YYYY-MM-DD");

export const DATE_PRESETS = [
  {
    key: "today",
    label: "Today",
    get: () => {
      const d = moment();
      return { startDate: fmt(d), endDate: fmt(d) };
    },
  },
  {
    key: "yesterday",
    label: "Yesterday",
    get: () => {
      const d = moment().subtract(1, "day");
      return { startDate: fmt(d), endDate: fmt(d) };
    },
  },
  {
    key: "this-month",
    label: "This Month",
    get: () => ({
      startDate: fmt(moment().startOf("month")),
      endDate: fmt(moment().endOf("day")),
    }),
  },
  {
    key: "last-month",
    label: "Last Month",
    get: () => {
      const m = moment().subtract(1, "month");
      return {
        startDate: fmt(m.clone().startOf("month")),
        endDate: fmt(m.clone().endOf("month")),
      };
    },
  },
  {
    key: "last-7",
    label: "Last 7 Days",
    get: () => ({
      startDate: fmt(moment().subtract(6, "days").startOf("day")),
      endDate: fmt(moment().endOf("day")),
    }),
  },
  {
    key: "last-30",
    label: "Last 30 Days",
    get: () => ({
      startDate: fmt(moment().subtract(29, "days").startOf("day")),
      endDate: fmt(moment().endOf("day")),
    }),
  },
];

export const matchPresetKey = (startDate, endDate) => {
  if (!startDate && !endDate) return "all";
  for (const p of DATE_PRESETS) {
    const r = p.get();
    if (r.startDate === startDate && r.endDate === endDate) return p.key;
  }
  return "custom";
};

export const formatUsd = (n) => {
  const num = Number(n) || 0;
  return `$${num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export const formatInr = (n) => {
  const num = Number(n) || 0;
  return `₹${num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export const formatUsdt = (n) => {
  const num = Number(n) || 0;
  return `${num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  })} USDT`;
};

export const formatNumber = (n) => {
  const num = Number(n) || 0;
  return num.toLocaleString();
};

export const saveBlobAsFile = (response, fallbackName) => {
  const blob = response?.data instanceof Blob ? response.data : new Blob([response?.data || ""], { type: "text/csv" });
  let filename = fallbackName;
  const cd = response?.headers?.["content-disposition"] || response?.headers?.["Content-Disposition"];
  if (cd) {
    const match = /filename="?([^";]+)"?/i.exec(cd);
    if (match && match[1]) filename = match[1];
  }
  saveAs(blob, filename);
};

const ExchangeIcon = ({ letter, bg }) => (
  <span style={{
    width: 24, height: 24, minWidth: 24, borderRadius: 7,
    background: bg, color: "#fff",
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    fontSize: 12, fontWeight: 800, flexShrink: 0,
  }}>{letter}</span>
);

export const KpiCard = ({ icon, label, value, color = "#1b5a90", highlight = false, breakdown = null, footer = null }) => {
  const showCoinDcx   = breakdown?.inr  != null;
  const showKuCoinRow = breakdown?.usdt != null;
  const total   = Math.abs(Number(breakdown?.inrTotal) || 0);
  const inrAmt  = Math.abs(Number(breakdown?.inr)      || 0);
  const hasBoth = showCoinDcx && breakdown?.usdt != null;

  let coinDcxPct = 0;
  if (total > 0 && showCoinDcx) coinDcxPct = (inrAmt / total) * 100;
  else if (!showCoinDcx && showKuCoinRow) coinDcxPct = 0;
  else if (showCoinDcx && !breakdown?.usdt) coinDcxPct = 100;
  const kuCoinPct = 100 - coinDcxPct;
  const barWidth  = hasBoth ? coinDcxPct : 100;

  const hasBottom = (breakdown?.inrTotal !== undefined) || footer;

  return (
    <div
      className="card h-100"
      style={{
        borderRadius: 16,
        border: highlight ? `2px solid ${color}` : "1px solid #e9ecef",
        boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
      }}
    >
      <div className="card-body" style={{ padding: "20px", display: "flex", flexDirection: "column" }}>
        {/* Top: icon + label + value */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 14, flex: 1 }}>
          <div style={{
            width: 52, height: 52, minWidth: 52, borderRadius: "50%",
            background: `${color}18`, border: `2px solid ${color}40`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 22, color, flexShrink: 0,
          }}>{icon}</div>
          <div>
            <div style={{ fontSize: 13, color: "#374151", fontWeight: 600, marginBottom: 6, lineHeight: 1.3 }}>
              {label}
            </div>
            <div style={{ fontSize: 24, fontWeight: 800, color: "#111827", lineHeight: 1 }}>
              {value}
            </div>
          </div>
        </div>

        {/* Bottom section */}
        {hasBottom && (
          <div style={{ marginTop: 16, paddingTop: 12, borderTop: "1px solid #f3f4f6" }}>

            {/* Exchange rows */}
            {breakdown?.inrTotal !== undefined && (
              <>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 10 }}>
                  {showCoinDcx && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <ExchangeIcon letter="₹" bg="#1b5a90" />
                      <span style={{ fontSize: 12, color: "#6b7280", width: 54, flexShrink: 0 }}>INR Pairs</span>
                      <span style={{ fontSize: 12, color: "#111827", fontWeight: 600, flex: 1 }}>{formatInr(breakdown.inr)}</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color, minWidth: 38, textAlign: "right" }}>
                        {coinDcxPct.toFixed(1)}%
                      </span>
                    </div>
                  )}
                  {showKuCoinRow && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <ExchangeIcon letter="$" bg="#e83e8c" />
                      <span style={{ fontSize: 12, color: "#6b7280", width: 54, flexShrink: 0 }}>USDT Pairs</span>
                      <span style={{ fontSize: 12, color: "#111827", fontWeight: 600, flex: 1 }}>
                        {breakdown.usdt != null ? formatUsdt(breakdown.usdt) : "0 USDT"}
                      </span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: "#e83e8c", minWidth: 38, textAlign: "right" }}>
                        {kuCoinPct.toFixed(1)}%
                      </span>
                    </div>
                  )}
                </div>
                {/* Progress bar */}
                <div style={{ height: 5, borderRadius: 99, background: "#f3f4f6", overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${barWidth}%`, background: color, borderRadius: 99, transition: "width 0.4s ease" }} />
                </div>
              </>
            )}

            {/* Footer row (Trades card) */}
            {footer && (
              <>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                  <span style={{ fontSize: 12, color: "#6b7280" }}>{footer.label}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: footer.color || color }}>{footer.value}</span>
                </div>
                <div style={{ height: 5, borderRadius: 99, background: "#f3f4f6", overflow: "hidden" }}>
                  <div style={{ height: "100%", width: "100%", background: footer.color || color, borderRadius: 99 }} />
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
